<template>
    <div 
        class="photo-upload"
        @dragover.prevent
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
    >
        <img 
            :src="currentPhotoUrl"
            @error="handleImageError"
            class="profile-photo"
            alt="Profile photo"
            referrerpolicy="no-referrer"
        />
        <div class="drop-zone" :class="{ 'drag-over': isDragging }">
            <p>Drag and drop your photo here or</p>
            <div class="photo-buttons">
                <button 
                    @click.prevent="triggerFileInput" 
                    type="button" 
                    class="change-photo-btn"
                >
                    Choose File
                </button>
                <button 
                    v-if="currentPhotoUrl !== DEFAULT_AVATAR" 
                    type="button"
                    @click="removePhoto" 
                    class="remove-photo-btn"
                >
                    Remove Photo
                </button>
            </div>
        </div>
        <input
            type="file"
            ref="fileInput"
            @change="handleFileSelect"
            accept="image/*"
            style="display: none"
        />
    </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { getStorage, ref as storageRef, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import defaultAvatar from '@/assets/default-avatar.png';
import { validatePhotoUrl } from '@/utils/photoUtils';
import { userService } from '@/services/api';

export default {
    name: 'PhotoUpload',
    props: {
        initialPhotoUrl: {
            type: String,
            required: true
        },
        userId: {
            type: String,
            required: true
        },
        isAdmin: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:photo', 'error'],
    setup(props, { emit }) {
        if (props.isAdmin && !props.userId) {
            console.error('PhotoUpload: Admin mode requires userId but none provided');
            return;
        }

        const currentPhotoUrl = ref(props.initialPhotoUrl);
        const DEFAULT_AVATAR = defaultAvatar;
        const photoImg = ref(null);
        const isLoading = ref(false);
        const fileInput = ref(null);
        const isDragging = ref(false);

        const triggerFileInput = () => {
            fileInput.value.click();
        };

        const handleFile = async (file) => {
            const targetUserId = props.userId;
            
            if (!props.userId) {
                console.error('PHOTO UPLOAD: No userId provided to component');
                return;
            }
            
            if (!file) return;
            
            try {
                const storage = getStorage();
                const storagePath = `profile-photos/${targetUserId}/${Date.now()}-${file.name}`;
                const fileRef = storageRef(storage, storagePath);
                
                const uploadTask = uploadBytesResumable(fileRef, file);
                
                uploadTask.on('state_changed', 
                    () => {
                        // Progress tracking removed
                    },
                    (error) => {
                        console.error('PHOTO UPLOAD: Upload failed:', error);
                    },
                    async () => {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        
                        try {
                            await userService.updateProfilePhoto(targetUserId, downloadURL, props.isAdmin);
                            emit('update:photo', downloadURL);
                        } catch (err) {
                            console.error('PHOTO UPLOAD: Database update failed:', err);
                            emit('error', err);
                        }
                    }
                );
            } catch (error) {
                console.error('PHOTO UPLOAD: Critical error:', error);
            }
        };

        const handleFileSelect = async (event) => {
            const file = event.target.files[0];
            if (file) {
                handleFile(file);
            }
        };

        const removePhoto = async () => {
            try {
                const targetUserId = props.isAdmin ? props.userId : props.userId;
                await userService.updateProfilePhoto(targetUserId, null);
                currentPhotoUrl.value = DEFAULT_AVATAR;
                emit('update:photo', DEFAULT_AVATAR);
            } catch (err) {
                console.error('Error removing photo:', err);
                emit('error', err);
            }
        };

        const handleImageError = () => {
            try {
                currentPhotoUrl.value = DEFAULT_AVATAR;
                emit('update:photo', DEFAULT_AVATAR);
            } catch (error) {
                console.error('Error in handleImageError:', error);
                currentPhotoUrl.value = DEFAULT_AVATAR;
                emit('update:photo', DEFAULT_AVATAR);
            }
        };

        const handleImageLoad = () => {
            // console.log('Photo loaded successfully:', {
            //     src: event.target.src,
            //     currentPhotoUrl: currentPhotoUrl.value,
            //     props: {
            //         initialPhotoUrl: props.initialPhotoUrl,
            //         userId: props.userId,
            //         isAdmin: props.isAdmin
            //     }
            // });
        };

        const handleDrop = (event) => {
            isDragging.value = false;
            const file = event.dataTransfer?.files[0];
            if (file) {
                // Validate file type
                const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
                if (!allowedTypes.includes(file.type)) {
                    emit('error', new Error('File must be JPEG, PNG, or GIF'));
                    return;
                }
                
                // Validate file size (max 2MB)
                const MAX_SIZE = 2 * 1024 * 1024;
                if (file.size > MAX_SIZE) {
                    emit('error', new Error('File size must be less than 2MB'));
                    return;
                }
                
                handleFile(file);
            }
        };

        onMounted(() => {
            currentPhotoUrl.value = props.initialPhotoUrl && props.initialPhotoUrl !== 'null' 
                ? props.initialPhotoUrl 
                : DEFAULT_AVATAR;
        });

        watch(() => props.initialPhotoUrl, (newUrl) => {
            if (newUrl) {
                currentPhotoUrl.value = newUrl;
            } else {
                currentPhotoUrl.value = DEFAULT_AVATAR;
            }
        });

        watch(currentPhotoUrl, async (newUrl) => {
            if (!newUrl) {
                currentPhotoUrl.value = DEFAULT_AVATAR;
                return;
            }
            
            if (newUrl === DEFAULT_AVATAR) {
                return;
            }
            
            try {
                const validatedUrl = await validatePhotoUrl(newUrl);
                if (!validatedUrl) {
                    console.warn('Invalid photo URL, setting to default avatar');
                    currentPhotoUrl.value = DEFAULT_AVATAR;
                } else {
                    currentPhotoUrl.value = validatedUrl;
                }
            } catch (error) {
                console.error('Error validating photo URL:', error);
                currentPhotoUrl.value = DEFAULT_AVATAR;
            }
        }, { immediate: true });

        return {
            currentPhotoUrl,
            DEFAULT_AVATAR,
            handleFileSelect,
            triggerFileInput,
            fileInput,
            removePhoto,
            handleImageError,
            photoImg,
            handleImageLoad,
            isLoading,
            handleDrop,
            isDragging,
            handleFile
        };
    }
};
</script>

<style scoped>
.photo-upload {
    text-align: center;
    margin-bottom: 20px;
    position: relative;
    width: 150px;
    margin-left: auto;
    margin-right: auto;
}

.profile-photo {
    width: 150px;
    height: 150px;
    object-fit: cover;
    border-radius: 50%;
    border: 2px solid #eee;
}

.drop-zone {
    position: absolute;
    top: -10px;
    left: -10px;
    width: calc(100% + 20px);
    height: calc(100% + 20px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    border: 2px dashed #ccc;
    border-radius: 12px;
    opacity: 0;
    transition: opacity 0.2s, border-color 0.2s;
}

.photo-upload:hover .drop-zone,
.drop-zone.drag-over {
    opacity: 1;
}

.drop-zone.drag-over {
    border-color: #4285f4;
    background: rgba(66, 133, 244, 0.1);
}

.drop-zone p {
    margin: 0 0 10px 0;
    color: #666;
    font-size: 0.9em;
    max-width: 140px;
}

.photo-buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
}

.change-photo-btn {
    display: inline-block;
    padding: 8px 16px;
    background-color: #4285f4;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    border: none;
    font-size: 14px;
    transition: background-color 0.2s;
    width: fit-content;
}

.remove-photo-btn {
    display: inline-block;
    padding: 8px 16px;
    background-color: #dc3545;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    border: none;
    font-size: 14px;
    transition: background-color 0.2s;
    width: fit-content;
}

.remove-photo-btn:hover {
    background-color: #c82333;
}

.change-photo-btn:hover {
    background-color: #3367d6;
}
</style> 